import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Save, Plus, X, Upload, ChevronUp, ChevronDown, RotateCcw } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useContent } from "@/context/ContentContext";

const ICON_OPTIONS = ["Sparkles", "ShieldCheck", "PiggyBank", "Home", "Award", "Leaf", "Sun", "Heart", "Star", "Package"];
const BANNER_STYLES = ["navy", "amber", "sky"];

function Field({ label, children, hint }) {
  return (
    <div>
      <label className="block text-xs uppercase font-bold tracking-widest text-slate-500 mb-2">{label}</label>
      {children}
      {hint && <p className="mt-1 text-xs text-slate-400">{hint}</p>}
    </div>
  );
}

function Input(props) {
  return <input {...props} className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-[#173a7a]" />;
}

function TextArea(props) {
  return <textarea rows={3} {...props} className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-[#173a7a] resize-y" />;
}

/** Image field with upload + URL controls. */
function ImageField({ value, onChange, label, testid }) {
  const [uploading, setUploading] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setUploading(false); }
  };
  return (
    <Field label={label}>
      <div className="flex items-start gap-3">
        <div className="w-24 h-24 rounded-xl bg-sky-50 border border-slate-100 overflow-hidden flex items-center justify-center flex-shrink-0">
          {value ? <img src={value} alt="" className="w-full h-full object-contain p-1" /> : <span className="text-xs text-slate-400">No image</span>}
        </div>
        <div className="flex-1 min-w-0 space-y-2">
          <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="/products/img.png or URL" data-testid={testid && `${testid}-url`} />
          <div className="flex gap-2">
            <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#173a7a] text-white text-xs font-semibold cursor-pointer">
              <Upload size={12} /> {uploading ? "Uploading…" : "Upload"}
              <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid={testid && `${testid}-upload`} />
            </label>
            {value && (
              <button type="button" onClick={() => onChange("")} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-slate-600 text-xs font-semibold">
                <X size={12} /> Clear
              </button>
            )}
          </div>
        </div>
      </div>
    </Field>
  );
}

function SectionCard({ title, description, children }) {
  return (
    <section className="rounded-2xl bg-white border border-slate-100 p-6 space-y-4">
      <div>
        <h3 className="font-display text-xl font-black text-[#0f2a5c]">{title}</h3>
        {description && <p className="text-sm text-slate-500 mt-1">{description}</p>}
      </div>
      {children}
    </section>
  );
}

export default function ContentEditor() {
  const { content, refresh } = useContent();
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (content) setDraft(JSON.parse(JSON.stringify(content)));
  }, [content]);

  const patch = useCallback((section, updater) => {
    setDraft((d) => ({ ...d, [section]: typeof updater === "function" ? updater(d[section]) : updater }));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content", draft);
      await refresh();
      toast.success("Content saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm("Reset ALL site content to defaults? This cannot be undone.")) return;
    setSaving(true);
    try {
      await api.post("/admin/content/reset");
      await refresh();
      toast.success("Content reset to defaults");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  if (!draft) return <div className="text-slate-400 py-10">Loading content…</div>;

  const move = (arr, i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= arr.length) return arr;
    const copy = [...arr];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    return copy;
  };

  return (
    <div data-testid="admin-content-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase tracking-widest text-amber-500 font-bold">CMS</p>
          <h1 className="mt-2 font-display text-3xl md:text-4xl font-black text-[#0f2a5c]">Site Content</h1>
          <p className="mt-1 text-sm text-slate-500">Edit homepage copy, images, pillars, FAQ, testimonials and footer links. Save publishes instantly.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={reset} data-testid="content-reset" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white border border-slate-200 text-slate-600 text-sm font-semibold">
            <RotateCcw size={14} /> Reset
          </button>
          <button onClick={save} disabled={saving} data-testid="content-save"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#173a7a] text-white text-sm font-semibold disabled:opacity-60">
            <Save size={14} /> {saving ? "Saving…" : "Save all"}
          </button>
        </div>
      </div>

      <div className="mt-8 space-y-6">
        {/* HERO */}
        <SectionCard title="Hero" description="Top of homepage.">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Small chip text"><Input value={draft.hero?.chip || ""} onChange={(e) => patch("hero", (h) => ({ ...h, chip: e.target.value }))} data-testid="hero-chip" /></Field>
            <Field label="Big headline"><Input value={draft.hero?.title || ""} onChange={(e) => patch("hero", (h) => ({ ...h, title: e.target.value }))} data-testid="hero-title-input" /></Field>
          </div>
          <Field label="Subtitle paragraph"><TextArea value={draft.hero?.subtitle || ""} onChange={(e) => patch("hero", (h) => ({ ...h, subtitle: e.target.value }))} data-testid="hero-subtitle-input" /></Field>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="CTA 1 — label"><Input value={draft.hero?.cta1_label || ""} onChange={(e) => patch("hero", (h) => ({ ...h, cta1_label: e.target.value }))} /></Field>
            <Field label="CTA 1 — href"><Input value={draft.hero?.cta1_href || ""} onChange={(e) => patch("hero", (h) => ({ ...h, cta1_href: e.target.value }))} /></Field>
            <Field label="CTA 2 — label"><Input value={draft.hero?.cta2_label || ""} onChange={(e) => patch("hero", (h) => ({ ...h, cta2_label: e.target.value }))} /></Field>
            <Field label="CTA 2 — href"><Input value={draft.hero?.cta2_href || ""} onChange={(e) => patch("hero", (h) => ({ ...h, cta2_href: e.target.value }))} /></Field>
          </div>
          <ImageField label="Right-side lifestyle image" value={draft.hero?.image} onChange={(v) => patch("hero", (h) => ({ ...h, image: v }))} testid="hero-image" />
        </SectionCard>

        {/* PRODUCT BANNERS */}
        <SectionCard title="Product Banners" description="Three large banners between Essentials and Truck.">
          {(draft.product_banners || []).map((b, i) => (
            <div key={i} className="rounded-xl bg-slate-50/60 p-4 space-y-3" data-testid={`banner-editor-${i}`}>
              <div className="flex items-center justify-between">
                <p className="font-bold text-sm text-slate-700">Banner {i + 1}</p>
                <div className="flex gap-1">
                  <button type="button" onClick={() => patch("product_banners", (a) => move(a, i, -1))} disabled={i === 0} className="p-1.5 rounded-lg bg-white border border-slate-200 disabled:opacity-40"><ChevronUp size={12} /></button>
                  <button type="button" onClick={() => patch("product_banners", (a) => move(a, i, 1))} disabled={i === (draft.product_banners.length - 1)} className="p-1.5 rounded-lg bg-white border border-slate-200 disabled:opacity-40"><ChevronDown size={12} /></button>
                  <button type="button" onClick={() => patch("product_banners", (a) => a.filter((_, idx) => idx !== i))} className="p-1.5 rounded-lg bg-white border border-slate-200 text-red-500"><X size={12} /></button>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <Field label="Product slug (links to /shop/…)"><Input value={b.slug || ""} onChange={(e) => patch("product_banners", (a) => a.map((x, idx) => idx === i ? { ...x, slug: e.target.value } : x))} /></Field>
                <Field label="Label"><Input value={b.label || ""} onChange={(e) => patch("product_banners", (a) => a.map((x, idx) => idx === i ? { ...x, label: e.target.value } : x))} /></Field>
                <Field label="Title"><Input value={b.title || ""} onChange={(e) => patch("product_banners", (a) => a.map((x, idx) => idx === i ? { ...x, title: e.target.value } : x))} /></Field>
                <Field label="Style"><select value={b.style || "sky"} onChange={(e) => patch("product_banners", (a) => a.map((x, idx) => idx === i ? { ...x, style: e.target.value } : x))} className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm bg-white">{BANNER_STYLES.map((s) => <option key={s} value={s}>{s}</option>)}</select></Field>
              </div>
              <Field label="Body"><TextArea value={b.body || ""} onChange={(e) => patch("product_banners", (a) => a.map((x, idx) => idx === i ? { ...x, body: e.target.value } : x))} /></Field>
              <ImageField label="Image" value={b.image} onChange={(v) => patch("product_banners", (a) => a.map((x, idx) => idx === i ? { ...x, image: v } : x))} />
            </div>
          ))}
          <button type="button" onClick={() => patch("product_banners", (a) => [...(a || []), { slug: "", label: "New", title: "New banner", body: "", image: "", style: "sky" }])} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white border border-dashed border-slate-300 text-sm font-semibold text-slate-600" data-testid="banner-add">
            <Plus size={14} /> Add banner
          </button>
        </SectionCard>

        {/* TRUCK */}
        <SectionCard title="Truck / Brand-at-Scale" description="Full-width banner with logistics message + factory & shelf photos.">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Chip"><Input value={draft.truck?.chip || ""} onChange={(e) => patch("truck", (t) => ({ ...t, chip: e.target.value }))} /></Field>
            <div />
            <Field label="Title line 1"><Input value={draft.truck?.title_line1 || ""} onChange={(e) => patch("truck", (t) => ({ ...t, title_line1: e.target.value }))} /></Field>
            <Field label="Title line 2 (amber)"><Input value={draft.truck?.title_line2 || ""} onChange={(e) => patch("truck", (t) => ({ ...t, title_line2: e.target.value }))} /></Field>
          </div>
          <Field label="Subtitle"><TextArea value={draft.truck?.subtitle || ""} onChange={(e) => patch("truck", (t) => ({ ...t, subtitle: e.target.value }))} /></Field>
          <div className="grid md:grid-cols-3 gap-4">
            <ImageField label="Truck image" value={draft.truck?.image_truck} onChange={(v) => patch("truck", (t) => ({ ...t, image_truck: v }))} />
            <ImageField label="Factory image" value={draft.truck?.image_factory} onChange={(v) => patch("truck", (t) => ({ ...t, image_factory: v }))} />
            <ImageField label="Shelf image" value={draft.truck?.image_shelf} onChange={(v) => patch("truck", (t) => ({ ...t, image_shelf: v }))} />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Factory title"><Input value={draft.truck?.factory_title || ""} onChange={(e) => patch("truck", (t) => ({ ...t, factory_title: e.target.value }))} /></Field>
            <Field label="Shelf title"><Input value={draft.truck?.shelf_title || ""} onChange={(e) => patch("truck", (t) => ({ ...t, shelf_title: e.target.value }))} /></Field>
            <Field label="Factory copy"><TextArea value={draft.truck?.factory_copy || ""} onChange={(e) => patch("truck", (t) => ({ ...t, factory_copy: e.target.value }))} /></Field>
            <Field label="Shelf copy"><TextArea value={draft.truck?.shelf_copy || ""} onChange={(e) => patch("truck", (t) => ({ ...t, shelf_copy: e.target.value }))} /></Field>
          </div>
        </SectionCard>

        {/* WHY - PILLARS */}
        <SectionCard title="Why Morning Soft — Pillars" description="Four benefits with icons.">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Eyebrow"><Input value={draft.why?.eyebrow || ""} onChange={(e) => patch("why", (w) => ({ ...w, eyebrow: e.target.value }))} /></Field>
            <Field label="Section title"><Input value={draft.why?.title || ""} onChange={(e) => patch("why", (w) => ({ ...w, title: e.target.value }))} /></Field>
          </div>
          {(draft.why?.pillars || []).map((p, i) => (
            <div key={i} className="rounded-xl bg-slate-50/60 p-4 grid md:grid-cols-4 gap-3 items-end" data-testid={`pillar-editor-${i}`}>
              <Field label="Icon"><select value={p.icon || "Sparkles"} onChange={(e) => patch("why", (w) => ({ ...w, pillars: w.pillars.map((x, idx) => idx === i ? { ...x, icon: e.target.value } : x) }))} className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm bg-white">{ICON_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}</select></Field>
              <Field label="Title"><Input value={p.title || ""} onChange={(e) => patch("why", (w) => ({ ...w, pillars: w.pillars.map((x, idx) => idx === i ? { ...x, title: e.target.value } : x) }))} /></Field>
              <div className="md:col-span-2 flex gap-2 items-end">
                <div className="flex-1">
                  <Field label="Description"><Input value={p.desc || ""} onChange={(e) => patch("why", (w) => ({ ...w, pillars: w.pillars.map((x, idx) => idx === i ? { ...x, desc: e.target.value } : x) }))} /></Field>
                </div>
                <div className="flex gap-1 pb-1">
                  <button onClick={() => patch("why", (w) => ({ ...w, pillars: move(w.pillars, i, -1) }))} disabled={i === 0} className="p-1.5 rounded-lg bg-white border border-slate-200 disabled:opacity-40"><ChevronUp size={12} /></button>
                  <button onClick={() => patch("why", (w) => ({ ...w, pillars: move(w.pillars, i, 1) }))} disabled={i === draft.why.pillars.length - 1} className="p-1.5 rounded-lg bg-white border border-slate-200 disabled:opacity-40"><ChevronDown size={12} /></button>
                  <button onClick={() => patch("why", (w) => ({ ...w, pillars: w.pillars.filter((_, idx) => idx !== i) }))} className="p-1.5 rounded-lg bg-white border border-slate-200 text-red-500"><X size={12} /></button>
                </div>
              </div>
            </div>
          ))}
          <button type="button" onClick={() => patch("why", (w) => ({ ...w, pillars: [...(w?.pillars || []), { icon: "Sparkles", title: "New pillar", desc: "" }] }))} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white border border-dashed border-slate-300 text-sm font-semibold text-slate-600" data-testid="pillar-add"><Plus size={14} /> Add pillar</button>
        </SectionCard>

        {/* FAQ */}
        <SectionCard title="FAQ" description="Add, edit, reorder or delete questions.">
          {(draft.faq || []).map((f, i) => (
            <div key={i} className="rounded-xl bg-slate-50/60 p-4 space-y-2" data-testid={`faq-editor-${i}`}>
              <div className="flex items-center justify-between">
                <p className="font-bold text-sm text-slate-700">Question {i + 1}</p>
                <div className="flex gap-1">
                  <button onClick={() => patch("faq", (a) => move(a, i, -1))} disabled={i === 0} className="p-1.5 rounded-lg bg-white border border-slate-200 disabled:opacity-40"><ChevronUp size={12} /></button>
                  <button onClick={() => patch("faq", (a) => move(a, i, 1))} disabled={i === draft.faq.length - 1} className="p-1.5 rounded-lg bg-white border border-slate-200 disabled:opacity-40"><ChevronDown size={12} /></button>
                  <button onClick={() => patch("faq", (a) => a.filter((_, idx) => idx !== i))} className="p-1.5 rounded-lg bg-white border border-slate-200 text-red-500"><X size={12} /></button>
                </div>
              </div>
              <Field label="Question"><Input value={f.q || ""} onChange={(e) => patch("faq", (a) => a.map((x, idx) => idx === i ? { ...x, q: e.target.value } : x))} data-testid={`faq-q-${i}`} /></Field>
              <Field label="Answer"><TextArea value={f.a || ""} onChange={(e) => patch("faq", (a) => a.map((x, idx) => idx === i ? { ...x, a: e.target.value } : x))} data-testid={`faq-a-${i}`} /></Field>
            </div>
          ))}
          <button type="button" onClick={() => patch("faq", (a) => [...(a || []), { q: "New question", a: "" }])} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white border border-dashed border-slate-300 text-sm font-semibold text-slate-600" data-testid="faq-add"><Plus size={14} /> Add FAQ</button>
        </SectionCard>

        {/* TESTIMONIALS */}
        <SectionCard title="Testimonials" description="Customer reviews on the homepage.">
          {(draft.testimonials || []).map((r, i) => (
            <div key={i} className="rounded-xl bg-slate-50/60 p-4 grid md:grid-cols-2 gap-3" data-testid={`testimonial-editor-${i}`}>
              <div className="md:col-span-2 flex items-center justify-between">
                <p className="font-bold text-sm text-slate-700">Review {i + 1}</p>
                <div className="flex gap-1">
                  <button onClick={() => patch("testimonials", (a) => move(a, i, -1))} disabled={i === 0} className="p-1.5 rounded-lg bg-white border border-slate-200 disabled:opacity-40"><ChevronUp size={12} /></button>
                  <button onClick={() => patch("testimonials", (a) => move(a, i, 1))} disabled={i === draft.testimonials.length - 1} className="p-1.5 rounded-lg bg-white border border-slate-200 disabled:opacity-40"><ChevronDown size={12} /></button>
                  <button onClick={() => patch("testimonials", (a) => a.filter((_, idx) => idx !== i))} className="p-1.5 rounded-lg bg-white border border-slate-200 text-red-500"><X size={12} /></button>
                </div>
              </div>
              <Field label="Name"><Input value={r.name || ""} onChange={(e) => patch("testimonials", (a) => a.map((x, idx) => idx === i ? { ...x, name: e.target.value } : x))} /></Field>
              <Field label="Role / subtitle"><Input value={r.role || ""} onChange={(e) => patch("testimonials", (a) => a.map((x, idx) => idx === i ? { ...x, role: e.target.value } : x))} /></Field>
              <div className="md:col-span-2"><Field label="Quote"><TextArea value={r.quote || ""} onChange={(e) => patch("testimonials", (a) => a.map((x, idx) => idx === i ? { ...x, quote: e.target.value } : x))} /></Field></div>
              <div className="md:col-span-2"><ImageField label="Avatar image (optional)" value={r.img} onChange={(v) => patch("testimonials", (a) => a.map((x, idx) => idx === i ? { ...x, img: v } : x))} /></div>
            </div>
          ))}
          <button type="button" onClick={() => patch("testimonials", (a) => [...(a || []), { name: "New Customer", role: "", quote: "", img: "" }])} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white border border-dashed border-slate-300 text-sm font-semibold text-slate-600" data-testid="testimonial-add"><Plus size={14} /> Add review</button>
        </SectionCard>

        {/* FOOTER */}
        <SectionCard title="Footer columns" description="Link groups shown in the site footer.">
          {(draft.footer_columns || []).map((col, ci) => (
            <div key={ci} className="rounded-xl bg-slate-50/60 p-4 space-y-3" data-testid={`footer-col-editor-${ci}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1">
                  <Field label="Column title"><Input value={col.title || ""} onChange={(e) => patch("footer_columns", (a) => a.map((x, idx) => idx === ci ? { ...x, title: e.target.value } : x))} /></Field>
                </div>
                <div className="flex gap-1 pt-6">
                  <button onClick={() => patch("footer_columns", (a) => move(a, ci, -1))} disabled={ci === 0} className="p-1.5 rounded-lg bg-white border border-slate-200 disabled:opacity-40"><ChevronUp size={12} /></button>
                  <button onClick={() => patch("footer_columns", (a) => move(a, ci, 1))} disabled={ci === draft.footer_columns.length - 1} className="p-1.5 rounded-lg bg-white border border-slate-200 disabled:opacity-40"><ChevronDown size={12} /></button>
                  <button onClick={() => patch("footer_columns", (a) => a.filter((_, idx) => idx !== ci))} className="p-1.5 rounded-lg bg-white border border-slate-200 text-red-500"><X size={12} /></button>
                </div>
              </div>
              <div className="space-y-2">
                {(col.links || []).map((l, li) => (
                  <div key={li} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 items-center">
                    <Input value={l.label || ""} placeholder="Label" onChange={(e) => patch("footer_columns", (a) => a.map((x, idx) => idx === ci ? { ...x, links: x.links.map((ll, lidx) => lidx === li ? { ...ll, label: e.target.value } : ll) } : x))} />
                    <Input value={l.to || ""} placeholder="/path or https://..." onChange={(e) => patch("footer_columns", (a) => a.map((x, idx) => idx === ci ? { ...x, links: x.links.map((ll, lidx) => lidx === li ? { ...ll, to: e.target.value } : ll) } : x))} />
                    <button onClick={() => patch("footer_columns", (a) => a.map((x, idx) => idx === ci ? { ...x, links: x.links.filter((_, lidx) => lidx !== li) } : x))} className="p-2 rounded-lg bg-white border border-slate-200 text-red-500"><X size={12} /></button>
                  </div>
                ))}
                <button onClick={() => patch("footer_columns", (a) => a.map((x, idx) => idx === ci ? { ...x, links: [...(x.links || []), { label: "New link", to: "/" }] } : x))} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-dashed border-slate-300 text-xs font-semibold text-slate-600"><Plus size={12} /> Add link</button>
              </div>
            </div>
          ))}
          <button onClick={() => patch("footer_columns", (a) => [...(a || []), { title: "New column", links: [] }])} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white border border-dashed border-slate-300 text-sm font-semibold text-slate-600"><Plus size={14} /> Add column</button>
        </SectionCard>

        {/* Sticky save */}
        <div className="sticky bottom-4 flex justify-end">
          <button onClick={save} disabled={saving} data-testid="content-save-bottom"
            className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#173a7a] text-white text-sm font-bold shadow-2xl disabled:opacity-60">
            <Save size={16} /> {saving ? "Saving…" : "Save all changes"}
          </button>
        </div>
      </div>
    </div>
  );
}
